import { Box, TextField, Typography } from '@mui/material';
import { format, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import emptyPage from '../../assets/img/empty-page.png';
import { LoadingPage } from '../../components/LoadingPage';
import { useUser } from '../../providers/userProvider';
import { customPalette } from '../../themes/customTheme';
import { apiUrl, httpClient } from '../../utils/httpUtils';
import { hasRole } from '../../utils/roles';
import { Evolution } from './Evolution';
import { Results } from './Results';
import { SummaryScore } from './SummaryScore';

export const ScoreList = () => {
  const t = useTranslate();
  const { team, userId, loading: userLoading } = useUser();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    format(subMonths(new Date(), 1), 'yyyy-MM', { locale: es })
  );

  const getResults = async () => {
    try {
      let params = [];
      if (team) {
        params.push(`team_id=${team}`);
      }
      if (userId && hasRole(['redactor']) && !hasRole(['editor'])) {
        params.push(`user_id=${userId}`);
      }
      if (selectedDate) {
        params.push(`month=${selectedDate}`);
      }
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const url = `${apiUrl}/scores/${hasRole(['redactor']) && !hasRole(['editor']) ? `members` : 'teams'}${queryString}`;
      const res = await httpClient(url);
      if (res.status === 200) {
        const result = await res.json.data;
        setData(result);
      }
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userLoading) {
      setLoading(true);
      getResults();
    }
  }, [selectedDate, team, userLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = event => {
    setSelectedDate(event.target.value);
  };

  if (userLoading || loading) return <LoadingPage />;

  return (
    <>
      {data ? (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '20px',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: 'center',
                fontSize: '32px',
                fontWeight: '700',
              }}
            >
              {t('resources.score.list.title')}
            </Typography>

            {!hasRole(['admin', 'gerente', 'editor']) ? null : (
              <TextField
                id="dateTo"
                label="Mes"
                type="month"
                InputLabelProps={{
                  shrink: true,
                }}
                value={selectedDate}
                onChange={handleDateChange}
                sx={{
                  label: {
                    color: customPalette.primary.main,
                    fontWeight: '500',
                  },
                }}
              />
            )}
          </Box>
          <Box>
            {!hasRole(['admin', 'gerente']) && data ? (
              <SummaryScore
                data={
                  !hasRole(['admin', 'gerente', 'editor'])
                    ? data[0]
                    : data?.team || {}
                }
              />
            ) : null}

            {hasRole(['admin', 'gerente']) && selectedDate ? (
              <Results
                title={t('resources.score.list.resultsTeam_title')}
                date={selectedDate}
              />
            ) : null}

            {hasRole(['admin', 'gerente', 'editor']) && selectedDate ? (
              <Results
                title={t('resources.score.list.resultsEditor_title')}
                members={true}
                date={selectedDate}
                newData={data?.members_scores}
              />
            ) : null}
            {!hasRole(['admin', 'gerente']) ? (
              <Evolution teamId={team} userId={userId} />
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '20px',
              marginBottom: '100px',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textAlign: 'center',
                fontSize: '32px',
                fontWeight: '700',
              }}
            >
              {t('resources.score.list.title')}
            </Typography>

            {!hasRole(['admin', 'gerente', 'editor']) ? null : (
              <TextField
                id="dateTo"
                label="Mes"
                type="month"
                InputLabelProps={{
                  shrink: true,
                }}
                value={selectedDate}
                onChange={handleDateChange}
                sx={{
                  label: {
                    color: customPalette.primary.main,
                    fontWeight: '500',
                  },
                }}
              />
            )}
          </Box>
          <img width={400} src={emptyPage} alt="Aun no hay datos" />
          <Box sx={{ maxWidth: '740px', margin: '0 auto' }}>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                fontSize: '24px',
                fontWeight: '600',
                marginTop: '16px',
              }}
            >
              {t('resources.score.list.empty_message')}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};
